// public/scripts/index-ui.js

// --- Imports ---
import { getFirebaseAuthService, getFirestoreService, getFunctionsService } from './krida-auth.js'; // For current user, services
import { httpsCallable } from "https://www.gstatic.com/firebasejs/10.12.2/firebase-functions.js";
import { getPlayerStats, getPlayerProgression } from './player-stats-client.js';
import { fetchLeaderboardData } from './leaderboard-client.js';
import { displayMyGames } from './my-games-client.js';
import { handleSignOut } from './index-auth.js'; // For the logout confirmation
import { openJoinGameModal, initJoinGameModal } from './join-game-modal.js';
import { openJoinTournamentModal, initJoinTournamentModal } from './tourny-join-modal.js';
import { fetchAndDisplayMyActiveTournaments, refreshMyTournamentsView } from './my-tournaments-client.js';
import { ThemeManager } from './theme-manager.js'; // <-- IMPORT THEME MANAGER
import { showUpgradePrompt } from './payment-client.js';

// --- DOM Element References (assigned in initializeUIEventListenersAndUpdates) ---
let newGameModal, humanOptionsDiv, computerOptionsDiv, inviteEmailSection;
let yourStatsBtn, yourStatsSummary, yourStatsModal, yourStatsModalContent, yourStatsDesktopContent;
let leaderboardBtn, yourRankSummary, leaderboardModal, leaderboardModalContent, leaderboardDesktopContent;
let gameHistoryModal, logoutConfirmModal, gameHistoryModalContent;
let optionsModal, themeSelectionContainer, saveOptionsBtn, cancelOptionsBtn; // <-- ADD OPTIONS MODAL ELEMENTS
let redeemCodeBtn, redeemCodeArea, redeemSubmitBtn, redeemCancelBtn, redeemCodeInput, redeemResult;

// --- Flag to store gameId from URL ---
let gameIdFromUrl = null;

// --- Assign DOM elements ---
function assignUiElements() {
    newGameModal = document.getElementById('new-game-modal');
    humanOptionsDiv = document.getElementById('human-options');
    computerOptionsDiv = document.getElementById('computer-options');
    inviteEmailSection = document.getElementById('invite-email-section');

    yourStatsBtn = document.getElementById('your-stats-btn');
    yourStatsSummary = document.getElementById('your-stats-summary');
    yourStatsModal = document.getElementById('your-stats-modal');
    yourStatsModalContent = document.getElementById('your-stats-modal-content');
    yourStatsDesktopContent = document.getElementById('your-stats-desktop-content');

    leaderboardBtn = document.getElementById('leaderboard-btn');
    yourRankSummary = document.getElementById('your-rank-summary');
    leaderboardModal = document.getElementById('leaderboard-modal');
    leaderboardModalContent = document.getElementById('leaderboard-modal-content');
    leaderboardDesktopContent = document.getElementById('leaderboard-desktop-content');

    gameHistoryModal = document.getElementById('game-history-modal');
    logoutConfirmModal = document.getElementById('logout-confirm-modal');
    gameHistoryModalContent = document.getElementById('game-history-modal-content');
    
    // Assign options modal elements
    optionsModal = document.getElementById('options-modal');
    themeSelectionContainer = document.getElementById('theme-selection-container');
    saveOptionsBtn = document.getElementById('save-options-btn');
    cancelOptionsBtn = document.getElementById('cancel-options-btn');

    // Assign redeem code elements
    redeemCodeBtn = document.getElementById('redeem-code-btn');
    redeemCodeArea = document.getElementById('redeem-code-area');
    redeemSubmitBtn = document.getElementById('redeem-submit-btn');
    redeemCancelBtn = document.getElementById('redeem-cancel-btn');
    redeemCodeInput = document.getElementById('redeem-code-input');
    redeemResult = document.getElementById('redeem-result');

    const urlParams = new URLSearchParams(window.location.search);
    const gameId = urlParams.get('gameId');
    const action = urlParams.get('action');

    if (gameId && action === 'join') {
        gameIdFromUrl = gameId;
        // Clean up the URL immediately, preserving other params and hash
        const newUrl = window.location.pathname + window.location.hash;
        window.history.replaceState({}, document.title, newUrl);
    }
    
    console.log("index-ui.js: UI elements assigned.");
}

// --- Modal Interaction Functions (Exported for use in index-main.js and HTML) ---
export function openModal(modalId) {
    console.log(`[index-ui.js] openModal called for ID: ${modalId}`);
    const modal = document.getElementById(modalId);
    if (modal) {
        console.log(`[index-ui.js] Modal element for ${modalId} found:`, modal);
        console.log(`[index-ui.js] Style before show for ${modalId}:`, modal.style.display);
        modal.style.display = 'block';
        console.log(`[index-ui.js] Style after show for ${modalId}:`, modal.style.display);
        // Close on background click
        modal.addEventListener('click', (event) => {
            if (event.target === modal) closeModal(modalId);
        });
    } else {
        console.error(`Modal with id ${modalId} not found!`);
    }
}

export function closeModal(modalId) {
    const modal = document.getElementById(modalId);
    if (modal) {
        modal.style.display = 'none';
    } else {
        console.error(`Modal with id ${modalId} not found!`);
    }
}

// --- NEW: Game Options Modal Logic ---
function populateThemeOptions() {
    if (!themeSelectionContainer || !ThemeManager) return;

    const allThemes = ThemeManager.getAllThemes();
    const unlockedThemes = ThemeManager.getAvailableThemes();
    const currentTheme = ThemeManager.getCurrentTheme();

    themeSelectionContainer.innerHTML = '';

    allThemes.forEach(theme => {
        const isUnlocked = unlockedThemes.includes(theme);
        const label = document.createElement('label');
        const input = document.createElement('input');
        input.type = 'radio';
        input.name = 'gameTheme';
        input.value = theme;
        if (theme === currentTheme) {
            input.checked = true;
        }
        if (!isUnlocked) {
            input.disabled = true;
            label.classList.add('locked');
        }

        const themeName = theme.charAt(0).toUpperCase() + theme.slice(1);
        const displayText = isUnlocked ? ` ${themeName}` : ` \u{1F512} ${themeName}`;

        label.appendChild(input);
        label.appendChild(document.createTextNode(displayText));
        themeSelectionContainer.appendChild(label);
    });
}

function openOptionsModal() {
    populateThemeOptions();
    openModal('options-modal');
}

function saveThemeSelection() {
    const selectedTheme = document.querySelector('input[name="gameTheme"]:checked')?.value;
    if (selectedTheme && ThemeManager) {
        ThemeManager.setTheme(selectedTheme);
        console.log(`Theme preference saved: ${selectedTheme}`);
    }
    closeModal('options-modal');
}
// --- END: Game Options Modal Logic ---

// --- Redeem Code Modal Logic ---
function openRedeemCodeModal() {
    if (redeemCodeInput) redeemCodeInput.value = '';
    if (redeemResult) {
        redeemResult.style.display = 'none';
        redeemResult.textContent = '';
        redeemResult.className = '';
    }
    if (redeemSubmitBtn) redeemSubmitBtn.disabled = false;
    openModal('redeem-code-modal');
}

async function submitRedeemCode() {
    const code = redeemCodeInput?.value?.trim();
    if (!code) {
        showRedeemResult('Please enter a promo code.', false);
        return;
    }

    if (redeemSubmitBtn) redeemSubmitBtn.disabled = true;
    if (redeemResult) redeemResult.style.display = 'none';

    try {
        const functions = getFunctionsService();
        const redeemPromoCode = httpsCallable(functions, 'redeemPromoCode');
        const result = await redeemPromoCode({ code });

        showRedeemResult(result.data.message || 'Paid tier unlocked!', true);

        // Dispatch tier-changed event (same as payment flow)
        window.dispatchEvent(new CustomEvent('krida-tier-changed', { detail: { tier: 'paid' } }));

        // Hide the redeem button since player is now paid
        if (redeemCodeArea) redeemCodeArea.style.display = 'none';

        // Close modal after brief delay
        setTimeout(() => closeModal('redeem-code-modal'), 2000);
    } catch (error) {
        const message = error.message || 'Failed to redeem code.';
        showRedeemResult(message, false);
        if (redeemSubmitBtn) redeemSubmitBtn.disabled = false;
    }
}

function showRedeemResult(message, isSuccess) {
    if (!redeemResult) return;
    redeemResult.textContent = message;
    redeemResult.className = isSuccess ? 'payment-success-message' : 'payment-error-message';
    redeemResult.style.display = 'block';
}

function updateRedeemCodeVisibility(tier) {
    if (redeemCodeArea) {
        redeemCodeArea.style.display = tier === 'paid' ? 'none' : '';
    }
}
// --- END: Redeem Code Modal Logic ---

// --- New Game Modal Logic ---
function handleNewGameClick() {
    console.log("[index-ui.js] handleNewGameClick called.");
    const auth = getFirebaseAuthService();
    if (!auth || !auth.currentUser) {
        alert("Please sign in first to create a new game.");
        return;
    }
    if (newGameModal) {
        openModal('new-game-modal');
    } else {
        console.error("New Game Modal element not found!");
    }
}

function closeNewGameModalLocal() {
    closeModal('new-game-modal');
}

function toggleOpponentOptionsLocal() {
    const humanRadio = document.getElementById('opponent-human');
    const newGameModalContent = newGameModal ? newGameModal.querySelector('.modal-content') : null;

    if (!humanOptionsDiv || !computerOptionsDiv || !newGameModal || !newGameModalContent) return; 

    const currentTheme = ThemeManager.getCurrentTheme();
    let backgroundImage = 'none';

    // Remove previous dynamic background styles and class
    newGameModalContent.style.backgroundImage = 'none';
    newGameModalContent.style.color = ''; // Reset text color
    newGameModalContent.style.fontWeight = ''; // Reset font weight
    newGameModalContent.classList.remove('has-dynamic-background');

    if (humanRadio && humanRadio.checked) {
        humanOptionsDiv.style.display = 'block';
        computerOptionsDiv.style.display = 'none';
        toggleInviteEmailLocal();
        // Set background for human opponent
        backgroundImage = `url('/themes/${currentTheme}/images/play-with-friends.png')`;
    } else {
        humanOptionsDiv.style.display = 'none';
        computerOptionsDiv.style.display = 'block';
        // Set background for computer opponent
        backgroundImage = `url('/themes/${currentTheme}/images/mighty-ai.jpg')`;
    }

    // Apply the background image to the modal content and add class for overlay
    if (backgroundImage !== 'none') {
        newGameModalContent.style.backgroundImage = backgroundImage;
        newGameModalContent.style.backgroundSize = 'cover';
        newGameModalContent.style.backgroundPosition = 'center center';
        newGameModalContent.style.backgroundRepeat = 'no-repeat';
        newGameModalContent.style.color = 'white'; // Set text color to white
        newGameModalContent.style.fontWeight = 'bold'; // Set font weight to bold
        newGameModalContent.classList.add('has-dynamic-background');
    }
}

function toggleInviteEmailLocal() {
    const specificInviteRadio = document.getElementById('invite-specific');
    if (!inviteEmailSection) return;

    if (specificInviteRadio && specificInviteRadio.checked) {
        inviteEmailSection.style.display = 'block';
    } else {
        inviteEmailSection.style.display = 'none';
    }
}

async function submitNewGameOptionsLocal() {
    const auth = getFirebaseAuthService();
    if (!auth || !auth.currentUser) {
        alert("Authentication error. Please sign in again.");
        return;
    }

    const opponentType = document.querySelector('input[name="opponentType"]:checked')?.value;
    const winningScore = parseInt(document.getElementById('winning-score')?.value) || 150;
    const bonusClearBoardInput = document.getElementById('bonus-clear')?.value;
    const bonusClearBoard = (bonusClearBoardInput !== '' && !isNaN(parseInt(bonusClearBoardInput))) ? parseInt(bonusClearBoardInput) : undefined;

    const specialCardsEnabled = document.getElementById('special-cards-toggle')?.checked ?? false;

    const options = {
        winningScore: winningScore,
        opponentType: opponentType,
        specialCardsEnabled: specialCardsEnabled
    };
    if (bonusClearBoard !== undefined) {
        options.bonusClearBoard = bonusClearBoard;
    }

    if (opponentType === 'human') {
        options.inviteType = document.querySelector('input[name="inviteType"]:checked')?.value;
        if (options.inviteType === 'specific') {
            options.invitedPlayerEmail = document.getElementById('invited-email')?.value;
            if (!options.invitedPlayerEmail || !options.invitedPlayerEmail.includes('@')) {
                alert("Please enter a valid email address for the specific player invite.");
                return;
            }
        }
    } else if (opponentType === 'computer') {
        options.aiDifficulty = document.getElementById('ai-difficulty')?.value;
        options.aiResponseSpeed = document.querySelector('input[name="aiResponseSpeed"]:checked')?.value || 'fast';
    }

    const submitButton = document.getElementById('create-game-submit-button');
    if (submitButton) submitButton.disabled = true;

    const functions = getFunctionsService();
    if (!functions) {
        alert("Error: Game creation service not available.");
        if (submitButton) submitButton.disabled = false;
        return;
    }

    try {
        const createGameFunction = httpsCallable(functions, 'createGame');
        const result = await createGameFunction({ options: options });
        const gameId = result.data?.gameId;

        if (gameId) {
            console.log(`New game created with ID: ${gameId}. Redirecting...`);
            closeNewGameModalLocal();
            window.location.href = `/krida.html?gameId=${gameId}`;
        } else {
            throw new Error("Failed to create game: Invalid response from server.");
        }
    } catch (error) {
        console.error("submitNewGameOptions: Error creating game:", error);
        alert(`Could not create game: ${error.message || "Unknown error"}`);
    } finally {
        if (submitButton) submitButton.disabled = false;
    }
}

// --- Player Stats UI Update ---
export async function updatePlayerStatsUI(userId) {
    console.log(`updatePlayerStatsUI called with userId: ${userId}`);
    
    // Check if UI elements exist, try to reassign if they don't
    if (!yourStatsModalContent || !yourStatsDesktopContent || !yourStatsSummary) {
        console.warn("Stats UI elements not found, attempting to reassign elements...");
        
        // Try to find elements again
        yourStatsModalContent = document.getElementById('your-stats-modal-content');
        yourStatsDesktopContent = document.getElementById('your-stats-desktop-content');
        yourStatsSummary = document.getElementById('your-stats-summary');
        
        // Log what we found
        console.log("After reassignment attempt:", {
            yourStatsModalContent: !!yourStatsModalContent,
            yourStatsDesktopContent: !!yourStatsDesktopContent,
            yourStatsSummary: !!yourStatsSummary
        });
    }
    
    if (!yourStatsModalContent || !yourStatsDesktopContent || !yourStatsSummary) {
        console.error("Stats UI elements still not found after reassignment. Element IDs might be incorrect or DOM not ready.");
        return;
    }

    if (!userId) {
        console.log("No userId provided, showing login message");
        if (yourStatsModalContent) yourStatsModalContent.innerHTML = '<p>Please log in to see your stats.</p>';
        if (yourStatsDesktopContent) yourStatsDesktopContent.innerHTML = '<h2>Your Stats</h2><p>Please log in to view your statistics.</p>';
        if (yourStatsSummary) yourStatsSummary.textContent = 'Win Rate: --%';
        return;
    }

    console.log("Loading stats for authenticated user:", userId);
    if (yourStatsModalContent) yourStatsModalContent.innerHTML = '<p>Loading stats...</p>';
    if (yourStatsDesktopContent) yourStatsDesktopContent.innerHTML = '<h2>Your Stats</h2><p>Loading...</p>';
    if (yourStatsSummary) yourStatsSummary.textContent = 'Loading...';

    try {
        // Fetch stats and progression in parallel; progression failure is non-fatal
        const [statsData, progressionData] = await Promise.all([
            getPlayerStats(userId),
            getPlayerProgression().catch(err => {
                console.error("updatePlayerStatsUI: Progression fetch failed (non-fatal):", err);
                return null;
            })
        ]);
        console.log("Stats data received:", statsData);
        console.log("Progression data received:", progressionData);

        // Check if player is tier-blocked and prompt for upgrade
        if (progressionData && progressionData.tierBlocked === true) {
            console.log("Player is tier-blocked — showing upgrade prompt");
            showUpgradePrompt();
        }

        // Show/hide redeem code button based on tier
        if (progressionData) {
            updateRedeemCodeVisibility(progressionData.tier);
        }

        let rankDisplayText = 'N/A';
        const auth = getFirebaseAuthService();

        if (auth && auth.currentUser) {
            try {
                const leaderboardForRank = await fetchLeaderboardData(100);
                if (leaderboardForRank.length > 0) {
                    const userRankIndex = leaderboardForRank.findIndex(p => p.userId === auth.currentUser.uid);
                    rankDisplayText = userRankIndex !== -1 ? `#${userRankIndex + 1}` : 'Unranked';
                }
            } catch (rankError) {
                console.error("updatePlayerStatsUI: Error fetching leaderboard for rank:", rankError);
                rankDisplayText = 'Error';
            }
        }

        let statsHtml = '';
        let winRateText = 'Win Rate: --%';

        if (statsData && typeof statsData.gamesPlayed === 'number') {
            if (statsData.gamesPlayed > 0) {
                const winRate = (statsData.wins / statsData.gamesPlayed) * 100;
                winRateText = `Win Rate: ${winRate.toFixed(0)}%`;
                statsHtml = `<div class="stat-list">
                    <div class="stat-item"><span class="stat-label">Games Played:</span><span class="stat-value">${statsData.gamesPlayed}</span></div>
                    <div class="stat-item"><span class="stat-label">Wins:</span><span class="stat-value">${statsData.wins}</span></div>
                    <div class="stat-item"><span class="stat-label">Losses:</span><span class="stat-value">${statsData.losses}</span></div>
                    <div class="stat-item"><span class="stat-label">Win Rate:</span><span class="stat-value">${winRate.toFixed(0)}%</span></div>
                    <div class="stat-item"><span class="stat-label">Rank:</span><span class="stat-value">${rankDisplayText}</span></div>
                </div>`;
            } else {
                winRateText = 'Win Rate: N/A';
                statsHtml = '<p>No games played yet.</p>';
            }
        } else {
            winRateText = 'Error';
            statsHtml = '<p>Could not load stats data.</p>';
        }

        // Append progression section if data available
        if (progressionData) {
            const isMaxLevel = progressionData.level >= 14;
            const percentage = isMaxLevel ? 100 :
                (progressionData.winsRequired > 0
                    ? Math.min(100, Math.round((progressionData.winsAtCurrentLevel / progressionData.winsRequired) * 100))
                    : 0);

            let progressContent;
            if (isMaxLevel) {
                progressContent = `
                    <div class="stat-item stat-progress">
                        <span class="stat-label">Progress:</span>
                        <span class="stat-value">Max Level Reached</span>
                    </div>`;
            } else {
                progressContent = `
                    <div class="stat-item stat-progress">
                        <span class="stat-label">Next Level:</span>
                        <span class="stat-value">${progressionData.winsAtCurrentLevel}/${progressionData.winsRequired} wins</span>
                    </div>
                    <div class="progress-bar-container">
                        <div class="progress-bar-fill" style="width: ${percentage}%"></div>
                    </div>`;
            }

            statsHtml += `<div class="stat-list stat-progression">
                <div class="stat-item stat-progression-header">
                    <span class="stat-label">Level:</span>
                    <span class="stat-value">${progressionData.level} (${progressionData.currentPhase})</span>
                </div>
                <div class="stat-item">
                    <span class="stat-label">Tier:</span>
                    <span class="stat-value"><span class="tier-badge ${progressionData.tier === 'paid' ? 'paid' : 'free'}">${progressionData.tier}</span></span>
                </div>
                ${progressContent}
            </div>`;
        }

        console.log("Updating stats UI with:", { statsHtml, winRateText });

        if (yourStatsModalContent) yourStatsModalContent.innerHTML = statsHtml;
        if (yourStatsDesktopContent) yourStatsDesktopContent.innerHTML = `<h2>Your Stats</h2>${statsHtml}`;
        if (yourStatsSummary) yourStatsSummary.textContent = winRateText;

        console.log("Stats UI update completed successfully");

    } catch (error) {
        console.error("Error updating player stats UI:", error);
        const errorMsg = `<p>${error.message || 'Error loading stats.'}</p>`;
        if (yourStatsModalContent) yourStatsModalContent.innerHTML = errorMsg;
        if (yourStatsDesktopContent) yourStatsDesktopContent.innerHTML = `<h2>Your Stats</h2>${errorMsg}`;
        if (yourStatsSummary) yourStatsSummary.textContent = 'Error';
    }
}

// --- Leaderboard UI Update ---
export async function updateLeaderboardUI() {
    if (!leaderboardModalContent || !leaderboardDesktopContent || !yourRankSummary) {
        // assignUiElements(); // Removed recursive call
        console.warn("Leaderboard UI elements not found, cannot update. This might indicate an issue with DOM loading or element IDs.");
        return;
    }
    const auth = getFirebaseAuthService();
    const currentUser = auth ? auth.currentUser : null;

    if (!currentUser) {
        if (leaderboardDesktopContent) leaderboardDesktopContent.innerHTML = '<h2>Leaderboard</h2><p>Please log in to view the leaderboard.</p>';
        if (leaderboardModalContent) leaderboardModalContent.innerHTML = '<p>Please log in to see the leaderboard.</p>';
        if (yourRankSummary) yourRankSummary.textContent = 'Log in for rank';
        return;
    }

    if (leaderboardModalContent) leaderboardModalContent.innerHTML = '<p>Loading leaderboard...</p>';
    if (leaderboardDesktopContent) leaderboardDesktopContent.innerHTML = '<h2>Leaderboard</h2><p>Loading...</p>';
    if (yourRankSummary) yourRankSummary.textContent = 'Loading...';

    try {
        let leaderboardData = [];
        let fullLeaderboard = []; // Declare in the higher scope
        let leaderboardHtml = '';
        try {
            // Fetch a larger list for accurate rank calculation, but only display the top few.
            fullLeaderboard = await fetchLeaderboardData(100); 
            leaderboardData = fullLeaderboard.slice(0, 4); // Keep only top 4 for the summary view

            if (leaderboardData.length > 0) {
                leaderboardHtml = '<div class="leaderboard-table-container">';
                leaderboardHtml += `<div class="leaderboard-header-row">
                                    <span class="leaderboard-col-rank">#</span><span class="leaderboard-col-name">Name</span>
                                    <span class="leaderboard-col-wins">Wins</span><span class="leaderboard-col-losses">Losses</span>
                                    <span class="leaderboard-col-winrate">Win %</span></div>`;
                leaderboardData.forEach((player, index) => {
                    const losses = (player.gamesPlayed ?? 0) - (player.wins ?? 0);
                    const winRate = player.gamesPlayed > 0 ? ((player.wins / player.gamesPlayed) * 100).toFixed(0) + '%' : 'N/A';
                    leaderboardHtml += `<div class="leaderboard-player-row">
                                        <span class="leaderboard-col-rank">${index + 1}</span><span class="leaderboard-col-name">${player.displayName ?? 'Unknown'}</span>
                                        <span class="leaderboard-col-wins">${player.wins ?? 0}</span><span class="leaderboard-col-losses">${losses < 0 ? 0 : losses}</span>
                                        <span class="leaderboard-col-winrate">${winRate}</span></div>`;
                });
                leaderboardHtml += '</div>';
            } else {
                leaderboardHtml = '<p>Leaderboard is empty.</p>';
            }
        } catch (fetchError) {
            console.error("Error fetching or processing leaderboard data:", fetchError);
            leaderboardHtml = `<p>${fetchError.message || 'Could not load leaderboard.'}</p>`;
            leaderboardData = [];
            fullLeaderboard = []; // Ensure it's empty on error too
        }

        if (leaderboardModalContent) leaderboardModalContent.innerHTML = leaderboardHtml;
        if (leaderboardDesktopContent) leaderboardDesktopContent.innerHTML = `<h2>Leaderboard</h2>${leaderboardHtml}`;

        let rankText = 'Rank: #--';
        if (currentUser && fullLeaderboard.length > 0) {
            const userRankIndex = fullLeaderboard.findIndex(p => p.userId === currentUser.uid);
            rankText = userRankIndex !== -1 ? `Rank: #${userRankIndex + 1}` : 'Rank: Unranked';
        }
        if (yourRankSummary) yourRankSummary.textContent = rankText;

    } catch (error) {
        console.error("Error updating leaderboard UI:", error);
        const errorMsg = '<p>Error loading leaderboard.</p>';
        if (leaderboardModalContent) leaderboardModalContent.innerHTML = errorMsg;
        if (leaderboardDesktopContent) leaderboardDesktopContent.innerHTML = `<h2>Leaderboard</h2>${errorMsg}`;
        if (yourRankSummary) yourRankSummary.textContent = 'Error';
    }
}

// --- Game History (Placeholder) ---
export async function loadGameHistory() {
    // assignUiElements(); // Removed call
    const auth = getFirebaseAuthService();
    if (!auth || !auth.currentUser) {
        if (gameHistoryModalContent) gameHistoryModalContent.innerHTML = '<p>Please log in to view your game history.</p>';
        return;
    }
    if (gameHistoryModalContent) gameHistoryModalContent.innerHTML = '<p>Loading game history...</p>';
    gameHistoryModalContent.innerHTML = '<p>Game history feature not yet implemented.</p>';
}

// --- My Games Display ---
export function clearGamesContent() {
    // assignUiElements(); // Removed recursive call
    const activeGamesList = document.getElementById('active-games-list');
    if (activeGamesList) {
        activeGamesList.innerHTML = '<p>Please log in to see your active games.</p>';
    }
    // ADDED: Clear tournaments list as well when clearing general game content if user logs out
    const tournamentsList = document.getElementById('tournaments-list');
    if (tournamentsList) {
        tournamentsList.innerHTML = '<p>Please log in to see your tournaments.</p>';
    }
}

// --- Initialize UI Event Listeners and Initial Updates ---
export function initializeUIEventListenersAndUpdates() {
    assignUiElements();
    ThemeManager.init(); // <-- INITIALIZE THE THEME MANAGER

    const newGameButton = document.getElementById('newGameBtn');
    if (newGameButton) newGameButton.addEventListener('click', handleNewGameClick);
    
    const openJoinGameModalButton = document.getElementById('openJoinGameModalBtn');
    if (openJoinGameModalButton) openJoinGameModalButton.addEventListener('click', openJoinGameModal);
    
    const openJoinTournamentModalButton = document.getElementById('openJoinTournamentModalBtn');
    if (openJoinTournamentModalButton) openJoinTournamentModalButton.addEventListener('click', openJoinTournamentModal);
    
    const closeNewGameBtn = document.querySelector('#new-game-modal .close-button');
    if (closeNewGameBtn) closeNewGameBtn.addEventListener('click', closeNewGameModalLocal);
    
    const opponentRadios = document.querySelectorAll('input[name="opponentType"]');
    opponentRadios.forEach(radio => radio.addEventListener('change', toggleOpponentOptionsLocal));
    
    const inviteRadios = document.querySelectorAll('input[name="inviteType"]');
    inviteRadios.forEach(radio => radio.addEventListener('change', toggleInviteEmailLocal));

    const createGameSubmitButton = document.getElementById('create-game-submit-button');
    if (createGameSubmitButton) createGameSubmitButton.addEventListener('click', submitNewGameOptionsLocal);
    
    const cancelNewGameButton = document.querySelector('#new-game-modal .form-actions button[type="button"]');
    if (cancelNewGameButton) cancelNewGameButton.addEventListener('click', closeNewGameModalLocal);

    if (newGameModal) { 
        toggleOpponentOptionsLocal(); 
        toggleInviteEmailLocal();
    }

    if (yourStatsBtn) yourStatsBtn.addEventListener('click', () => openModal('your-stats-modal'));
    if (leaderboardBtn) leaderboardBtn.addEventListener('click', () => openModal('leaderboard-modal'));
    
    const closeStatsModalBtn = document.querySelector('#your-stats-modal .close-button');
    if(closeStatsModalBtn) closeStatsModalBtn.addEventListener('click', () => closeModal('your-stats-modal'));

    const closeLeaderboardModalBtn = document.querySelector('#leaderboard-modal .close-button');
    if(closeLeaderboardModalBtn) closeLeaderboardModalBtn.addEventListener('click', () => closeModal('leaderboard-modal'));

    const confirmSignOutBtn = document.getElementById('confirm-sign-out-btn');
    if (confirmSignOutBtn) confirmSignOutBtn.addEventListener('click', handleSignOut);

    const closeLogoutModalBtn = document.querySelector('#logout-confirm-modal .close-button');
    if(closeLogoutModalBtn) closeLogoutModalBtn.addEventListener('click', () => closeModal('logout-confirm-modal'));
    
    const cancelLogoutBtn = document.querySelector('#logout-confirm-modal button.btn-secondary');
    if(cancelLogoutBtn) cancelLogoutBtn.addEventListener('click', () => closeModal('logout-confirm-modal'));

    const closeGameHistoryModalBtn = document.querySelector('#game-history-modal .close-button');
    if(closeGameHistoryModalBtn) closeGameHistoryModalBtn.addEventListener('click', () => closeModal('game-history-modal'));

    // --- NEW: Options Modal Listeners ---
    if (saveOptionsBtn) {
        saveOptionsBtn.addEventListener('click', saveThemeSelection);
    }
    if (cancelOptionsBtn) {
        cancelOptionsBtn.addEventListener('click', () => closeModal('options-modal'));
    }
    // TODO: A button to open this modal needs to be added, likely in the nav drawer.
    // For now, you can open it from the console with `openOptionsModal()` for testing.
    // Example hookup (would go in nav-drawer-manager.js or similar):
    // const optionsBtn = document.getElementById('nav-options-btn');
    // if (optionsBtn) optionsBtn.addEventListener('click', openOptionsModal);
    // --- END: Options Modal Listeners ---

    // --- Redeem Code Modal Listeners ---
    if (redeemCodeBtn) redeemCodeBtn.addEventListener('click', openRedeemCodeModal);
    if (redeemSubmitBtn) redeemSubmitBtn.addEventListener('click', submitRedeemCode);
    if (redeemCancelBtn) redeemCancelBtn.addEventListener('click', () => closeModal('redeem-code-modal'));
    const closeRedeemModalBtn = document.querySelector('#redeem-code-modal .close-button');
    if (closeRedeemModalBtn) closeRedeemModalBtn.addEventListener('click', () => closeModal('redeem-code-modal'));
    // Allow Enter key to submit
    if (redeemCodeInput) {
        redeemCodeInput.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') submitRedeemCode();
        });
    }
    // --- END: Redeem Code Modal Listeners ---

    // Listen for tier change after successful payment
    window.addEventListener('krida-tier-changed', () => {
        const auth = getFirebaseAuthService();
        if (auth && auth.currentUser) {
            updatePlayerStatsUI(auth.currentUser.uid);
        }
    });

    const tabButtons = document.querySelectorAll('.tab-navigation .tab-button');
    const tabPanels = document.querySelectorAll('.tab-content .tab-panel');

    function setActiveTab(targetTabId) {
        tabButtons.forEach(btn => {
            btn.classList.remove('active');
            if (btn.dataset.tab === targetTabId) {
                btn.classList.add('active');
            }
        });
        tabPanels.forEach(panel => {
            panel.classList.remove('active');
            if (panel.id === targetTabId) {
                panel.classList.add('active');
                // Call data loading functions based on the activated tab
                if (targetTabId === 'single-games-panel') {
                    console.log("Activating Single Games tab, calling displayMyGames()");
                    displayMyGames();
                } else if (targetTabId === 'tournaments-panel') {
                    console.log("Activating Tournaments tab, calling fetchAndDisplayMyActiveTournaments()");
                    fetchAndDisplayMyActiveTournaments();
                }
            }
        });
    }

    tabButtons.forEach(button => {
        button.addEventListener('click', () => {
            const targetPanelId = button.dataset.tab;
            if (targetPanelId) {
                setActiveTab(targetPanelId);
                // Update URL hash for tab persistence/bookmarking (optional, but good UX)
                // window.location.hash = targetPanelId;
            }
        });
    });

    // Check for URL parameters to set active tab on load
    const urlParamsForTab = new URLSearchParams(window.location.search);
    const tabQueryParam = urlParamsForTab.get('tab');
    let initialTabId = 'single-games-panel'; // Default tab

    if (tabQueryParam === 'tournaments') {
        initialTabId = 'tournaments-panel';
    }
    // Add more else if blocks here if you introduce more tabs that can be deep-linked

    setActiveTab(initialTabId);
    // If you were using hash for tabs, you'd check window.location.hash here as well or instead.

    // Initial UI state for logged-out user (auth state change will update this if logged in)
    updatePlayerStatsUI(null);
    updateLeaderboardUI();
    clearGamesContent(); // This will set initial "Please log in" messages

    // --- Initialize all modals that are loaded from separate HTML files ---
    initJoinGameModal();
    initJoinTournamentModal();

    console.log("index-ui.js: Initialized UI event listeners and initial content.");
}

// --- New function to handle actions from URL parameters after auth
export async function handleActionFromUrl() {
    const urlParams = new URLSearchParams(window.location.search);
    const gameId = gameIdFromUrl; // Use the flag set during element assignment
    const shouldOpenNewGameModal = urlParams.get('openNewGameModal') === 'true';
    const tabToOpen = urlParams.get('tab');

    // Determine if any action needs to be taken that requires authentication
    const needsAuthAction = gameId || shouldOpenNewGameModal;

    if (needsAuthAction) {
        // Check if user is already authenticated
        const auth = getFirebaseAuthService();
        const currentUser = auth?.currentUser;
        
        if (currentUser) {
            // User is already logged in, perform actions immediately
            console.log(`handleActionFromUrl: User is already authenticated, performing actions immediately.`);
            if (gameId) {
                console.log(`handleActionFromUrl: User is authenticated, attempting to join game ${gameId}.`);
                openJoinGameModal(gameId);
            }
            if (shouldOpenNewGameModal) {
                console.log(`handleActionFromUrl: User is authenticated, opening new game modal.`);
                handleNewGameClick(); // This function already checks for auth, but we've confirmed it here
            }
        } else {
            // User is not logged in, wait for authentication to be resolved
            console.log(`handleActionFromUrl: User not authenticated, waiting for auth state change.`);
            const authListener = (event) => {
                const user = event.detail.user;
                if (user) {
                    // User is logged in, perform actions
                    if (gameId) {
                        console.log(`handleActionFromUrl: User is authenticated, attempting to join game ${gameId}.`);
                        openJoinGameModal(gameId);
                    }
                    if (shouldOpenNewGameModal) {
                        console.log(`handleActionFromUrl: User is authenticated, opening new game modal.`);
                        handleNewGameClick(); // This function already checks for auth, but we've confirmed it here
                    }
                } else {
                    // User is not logged in
                    if (gameId) {
                        alert("Please log in to join the game.");
                    }
                    if (shouldOpenNewGameModal) {
                        alert("Please log in to create a new game.");
                    }
                }
            };
            document.addEventListener('krida-auth-state-changed', authListener, { once: true });
        }
    }

    // Handle tab switching immediately, as it doesn't require auth
    if (tabToOpen) {
        setActiveTab(`${tabToOpen}-panel`);
    }

    // Clean up all handled URL parameters
    let searchParamsChanged = false;
    const newParams = new URLSearchParams(window.location.search);

    if (gameId) {
        // gameIdFromUrl is a module variable, not in query params anymore, so no need to delete
    }
    if (shouldOpenNewGameModal) {
        newParams.delete('openNewGameModal');
        searchParamsChanged = true;
    }
    if (tabToOpen) {
        newParams.delete('tab');
        searchParamsChanged = true;
    }

    if (searchParamsChanged) {
        const newQueryString = newParams.toString();
        const newUrl = window.location.pathname + (newQueryString ? `?${newQueryString}` : '') + window.location.hash;
        window.history.replaceState({}, document.title, newUrl);
    }
}